import { useQuery } from "@tanstack/react-query";
import ShivversService from "src/api/ShivversService";
import { ExtendedColumnType, useColumnExpansion } from "../binOverview/BinCommander";
import AdminUserSearchDTO from "src/models/AdminUserSearchDTO";
import { Button, Col, Input, Row, Select, Space, Table, Typography } from "antd";
import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useLocale } from "antd/es/locale";
import Routes from "src/consts/Routes";
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { DEBOUNCE_TIME_MS } from "./UserList";
import dayjs from "dayjs";
import { NavLink } from "react-router-dom";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import RoleUtil from "src/utils/RoleUtil";
import { CSVLink } from "react-csv";



export const PremierUserList = () => {
    const [nameSearch, setNameSearch] = useState("");
    const nameSearchDelayedValue = useDebouncedCallback((value) => {
        setNameSearch(value);
    }, DEBOUNCE_TIME_MS);

    const [securityFilter, setSecurityFilter] = useState<number[]>([]);


    const history = useHistory();
    const location = useLocation();

    const formatRegion = (value: number | null | undefined) => {
        if (value == null) {
            return null;
        }
        if (value === 1100) {
            return "1100 Glen Muench";
        }
        else if (value === 1700) {
            return "1700 Mark Sexton";
        }
        else if (value === 2000) {
            return "2000 Dustin Beadnell";
        }
        else if (value === 2100) {
            return "2100 Kyle Canady";
        }
        else if (value === 2900) {
            return "2900 Scott Graham";
        }
        else if (value === 3000) {
            return "3000 Tom Barron";
        }
        else if (value === 3300) {
            return "3300 BJ Arbuckle";
        }
        else {
            return `${value}`;
        }
    }

    const formatActivationData = (value: string | null | undefined) => {
        if (!value) {
            return value;
        }
        const parsed = dayjs(value);
        if (!parsed.isValid()) {
            return value;
        }
        return dayjs(value).tz("America/Chicago", false)?.format('MMM D YYYY');
    }

    const query = useQuery({
        queryKey: [{purpose: "userSearch", scope: "userEdit"}] as const,
        queryFn: async (q) => {
            var res =  await ShivversService.searchUsers(undefined, undefined, q.signal);
            const extended: Array<typeof res[0] & {region: string | undefined | null, activationDate: string | null | undefined}> = res.map(user => {
                return {
                    ...user,
                    region: formatRegion(user.regionId),
                    activationDate: formatActivationData(user.activationDateIso8601),
                    linkInactiveDate: formatActivationData(user.linkInactiveDateIso8601),
                }
            });
            return extended;
        }
    });

    const columns: Array<ExtendedColumnType<AdminUserSearchDTO>> = useMemo(() => [
        {
            dataIndex: "internalId",
            hidden: true,
        },
        {
            dataIndex: "externalId",
            hidden: true,
        },
        {
            dataIndex: "username",
            title: "Username",
            hidden: true,
        },
        {
            title: "Account",
            dataIndex: "account",
            sortType: 'string',
        },
        {
            title: "Last_Name",
            dataIndex: "lastName",
            defaultSortOrder: "ascend",
            sortType: 'string',
            filterSearch: true,
            render(value, record, index) {
                if (record.security !== 1) {
                    return <span>{value}</span>
                }
                return <div>
                    <Typography.Link href={`${Routes.BIN_STATS_SHIVVERS}/${record['externalId']}`} target="_blank">{value}</Typography.Link>
                </div>
            },
        },
        {
            title: "First_Name",
            dataIndex: "firstName",
            sortType: 'string',
        },
        {
            title: "Address",
            dataIndex: "address",
            sortType: 'string',
        },
        {
            title: "City",
            dataIndex: "city",
            sortType: 'string',
        },
        {
            title: 'State',
            dataIndex: 'state',
            sortType: 'string',
        },
        {
            title: 'Dealer',
            dataIndex: 'dealer',
            sortType: 'string',
        },
        {
            title: "DSM",
            dataIndex: "region",
            sortType: "string",
            render(value, record, index) {
                return formatRegion(record.regionId);
            },
        },
        {
            title: 'Premier',
            dataIndex: 'premier',
            sortType: 'string',
        },
        {
            title: 'Activation_Date',
            dataIndex: 'activationDate',
            render: (value, record, index) => {
                return formatActivationData(record.activationDateIso8601);
            },
            sorter: (a, b, sortOrder) => {
                // put nulls at end of sorts
                // https://github.com/TanStack/table/discussions/2371#discussioncomment-210260
                if (!a.activationDateIso8601 && !b.activationDateIso8601) {
                    return 0;
                }

                if (!a.activationDateIso8601) {
                    return sortOrder === "descend" ? -1 : 1;
                }

                if (!b.activationDateIso8601) {
                    return sortOrder === "descend" ? 1 : -1;
                }
                return dayjs(a.activationDateIso8601).diff(b.activationDateIso8601);
            }
        },
        {
            title: 'Inactive_Date',
            dataIndex: 'linkInactiveDate',
            render: (value, record, index) => {
                return formatActivationData(record.linkInactiveDateIso8601);
            },
            sorter: (a, b, sortOrder) => {
                // put nulls at end of sorts
                // https://github.com/TanStack/table/discussions/2371#discussioncomment-210260
                if (!a.linkInactiveDateIso8601 && !b.linkInactiveDateIso8601) {
                    return 0;
                }

                if (!a.linkInactiveDateIso8601) {
                    return sortOrder === "descend" ? -1 : 1;
                }

                if (!b.linkInactiveDateIso8601) {
                    return sortOrder === "descend" ? 1 : -1;
                }
                return dayjs(a.linkInactiveDateIso8601).diff(b.linkInactiveDateIso8601);
            }
        },
        {
            hidden: true,
            title: "Inactive_Notes",
            dataIndex: "inactiveNotes",
        },
        {
            dataIndex: "security",
            title: "Security",
            sortType: "number",
        },
        {
            width: 153,
            fixed: "right" as const,
            title: "Details",
            hidden: !RoleUtil.currentUserIsAdmin(),
            render(value, record, index) {
                return <NavLink to={Routes.generate(Routes.USERS_DETAIL, { id: record.internalId })}>View / Edit <EditOutlined /></NavLink>
            },
        },

    ], [query.data]);






    let transformed = useMemo(() => useColumnExpansion({ columns: columns, datasource: query.data ?? [] }), [query.data, columns]);
    const updateFilters = () => {
        if (nameSearch === "" && securityFilter.length === 0) {
            return query.data ?? [];
        }

        const filteredTable = query.data
            ?.filter((record) => {
                if (nameSearch == "") {
                    return true;
                }

                return record.lastName?.toUpperCase()?.includes(nameSearch.toUpperCase())
            || record.firstName?.toUpperCase()?.includes(nameSearch.toUpperCase())
            || record.dealer?.toUpperCase()?.includes(nameSearch.toUpperCase())
            || record.account?.toUpperCase().includes(nameSearch.toUpperCase())}
        )?.filter((record) => {
            if (securityFilter.length === 0) {
                return true;
            }
            return securityFilter.includes(record.security!);
        }
         )
            ?? [];
        return filteredTable;
    };

    const finalTable = useMemo(() => updateFilters(), [nameSearch, securityFilter, query.data]);

    return <>

        <Row gutter={[16, 16]}>
            <Col xs={24}>
                <Space wrap direction="horizontal" size="middle">
                    <Input style={{ width: 240 }} onChange={(evt) => nameSearchDelayedValue(evt.target.value)} placeholder='Search by Name or Account'></Input>
                    <Input style={{ width: 240 }} onChange={(evt) => {
                        const parsed = Number.parseInt(evt.target.value);
                        if (!Number.isInteger(parsed)) {
                            setSecurityFilter([]);
                            return;
                        }
                        setSecurityFilter([parsed])}
                        } placeholder='Search by Security'></Input>

                        <CSVLink data={finalTable} filename={`shivvers_users_list_${dayjs().format("YYYY-MM-DD HH_mm_ss")}.csv`}
                            headers={[
                                ...columns
                                    .filter(col => (!col.hidden || col.dataIndex === "username" || col.dataIndex === "linkInactiveDate" || col.dataIndex === "security" || col.dataIndex === "inactiveNotes") && col.dataIndex)
                                    .filter(col => col.dataIndex != "internalId" && col.dataIndex != "externalId"
                                    )
                                    .map(col => ({
                                        key: col.dataIndex as string,
                                        label: col.title as string
                                    }))
                            ]}
                        >
                            <Button icon={<DownloadOutlined />}>Download CSV</Button>
                        </CSVLink>
                </Space>
            </Col>
            <Col xs={24}>
                <Table onRow={(data, index) => {
                    return {
                        // onClick: (d) => {
                        //     if (data.security !== 1) {
                        //         return;
                        //     }
                        //     const binStatsRoute = `${Routes.BIN_STATS_SHIVVERS}/${data['internalId']}`;

                        //     history.push(binStatsRoute);
                        // }
                    }
                }}
                    size="small" sticky scroll={{x: 1200}} rowKey="externalId" pagination={{ defaultPageSize: 200, size: "default" }} loading={query.isLoading} dataSource={finalTable ?? []} columns={transformed}

                />
            </Col>
        </Row>

    </>;
}